/**
 * TanStack Query Hook for Funding Trends KPI
 *
 * This hook provides access to funding trends KPI data from the database,
 * including total funding and year-over-year percentage change.
 *
 * Database Source: web.v_kpi_funding_trends_v1 view
 *
 * @module useKpiFundingTrendsV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Funding Trends KPI data
 */
interface KpiFundingTrends {
  total_funding_billions_eur: number;
  funding_change_percentage: number;
}

/**
 * Fetches funding trends KPI data from the database
 *
 * @returns {Promise<KpiFundingTrends>} KPI data object with funding amount and change percentage
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiFundingTrends = async (): Promise<KpiFundingTrends> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_funding_trends_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch funding trends KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from funding trends KPI query');
  }

  return data;
};

/**
 * React Hook for Funding Trends KPI Data
 *
 * Fetches and caches the funding trends KPI data using TanStack Query.
 * Includes total funding in billions EUR and year-over-year change percentage.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'funding-trends', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiFundingTrends>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiFundingTrendsV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>Total Funding: €{data.total_funding_billions_eur}B</p>
 *     <p>Change: {data.funding_change_percentage}%</p>
 *   </div>
 * );
 * ```
 */
export const useKpiFundingTrendsV1 = (): UseQueryResult<KpiFundingTrends> => {
  return useQuery<KpiFundingTrends>({
    queryKey: ['kpi', 'funding-trends', 'v1'],
    queryFn: fetchKpiFundingTrends,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
